import React from 'react';
import styled from 'styled-components';
import dayjs from 'dayjs';
import userApis from 'features/user/services/apis';
import { useEffect, useState } from 'react';
import { Avatar } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import shopSelectors from 'services/shop/selectors';
import { formatCityStateZip } from 'features/report/pages/ReportPage/utils';

type ITimeTrackerPrintPreviewProps = {
  date?: string;
  staffId?: string;
};

type ITimeTracker = {
  staffName: string;
  avatar: string;
  time: string;
  type: 'CLOCK_IN' | 'CLOCK_OUT';
  clockInTime: string;
  clockOutTime: string;
  workingTime: number;
};

const TimeTrackerPrintPreview = ({ date, staffId }: ITimeTrackerPrintPreviewProps) => {
  const [data, setData] = useState<ITimeTracker[]>([]);
  const shopInfo = shopSelectors.data.shopInfo();

  const fetchData = async (date?: string, staffId?: string) => {
    try {
      const _date = date ? date : dayjs().format('MM-DD-YYYY');
      const res = await userApis.getTimeTracker(_date, staffId || '');
      if (res?.data?.data?.content) {
        setData(res.data.data.content);
      } else {
        setData([]);
      }
    } catch (error) {
      setData([]);
    }
  };

  useEffect(() => {
    if (!date || !dayjs(date).isValid()) return;
    fetchData(date, staffId);
  }, [date, staffId]);

  const groupedData = data.reduce((acc, item) => {
    if (!acc[item.staffName]) {
      acc[item.staffName] = [];
    }
    acc[item.staffName].push(item);
    return acc;
  }, {} as Record<string, ITimeTracker[]>);

  const getTotalHours = (staffData: ITimeTracker[]) => {
    const total = staffData.reduce((total, item) => total + (item.workingTime || 0), 0);
    return Math.round((total + Number.EPSILON) * 100) / 100;
  };

  return (
    <PrintContainer>
      <HeaderSection>
        <ShopName>{shopInfo?.shopName}</ShopName>
        <ShopAddress>{shopInfo?.address} {formatCityStateZip(shopInfo?.city, shopInfo?.state, shopInfo?.zip)}</ShopAddress>
        <ShopPhone>{shopInfo?.phone}</ShopPhone>
        <DateText>{date}</DateText>
        <Divider />
      </HeaderSection>

      <ContentSection>
        {Object.entries(groupedData).map(([staffName, staffData], index) => {
          const totalHours = getTotalHours(staffData);
          
          return (
            <StaffSection key={staffName}>
              <StaffHeader>
                <StaffName>{staffName.toUpperCase()}: {totalHours} HOURS</StaffName>
              </StaffHeader>
              
              <TimeTable>
                <TableHeader>
                  <HeaderCell isFirstColumn>CLOCK IN</HeaderCell>
                  <HeaderCell>CLOCK OUT</HeaderCell>
                  <HeaderCell>HOURS</HeaderCell>
                </TableHeader>
                
                {staffData.map((item, itemIndex) => (
                  <TableRow key={itemIndex}>
                    <TableCell isFirstColumn>{item.clockInTime || '--'}</TableCell>
                    <TableCell>{item.clockOutTime || '--'}</TableCell>
                    <TableCell>{item.workingTime ? `${item.workingTime}H` : '--'}</TableCell>
                  </TableRow>
                ))}
              </TimeTable>
              
              {index < Object.keys(groupedData).length - 1 && <SectionDivider />}
            </StaffSection>
          );
        })}
      </ContentSection>
    </PrintContainer>
  );
};

export default TimeTrackerPrintPreview;

const PrintContainer = styled.div`
  background: white;
  font-family: 'Open Sans', Arial, sans-serif;
  width: 100%;
  height: fit-content;
`;

const HeaderSection = styled.div`
  text-align: center;
  padding: 4px;
`;

const ShopName = styled.div`
  font-size: 18px;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 2px;
  color: black;
`;

const ShopAddress = styled.div`
  font-size: 14px;
  margin-bottom: 2px;
  color: black;
`;

const ShopPhone = styled.div`
  font-size: 14px;
  margin-bottom: 4px;
  color: black;
`;

const DateText = styled.div`
  font-size: 18px;
  font-weight: bold;
  margin-top: 4px;
  color: black;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid black;
  margin: 4px 8px;
`;

const ContentSection = styled.div`
  margin: 0 8px;
`;

const StaffSection = styled.div`
  margin-bottom: 16px;
`;

const StaffHeader = styled.div`
  background-color: black;
  color: white;
  padding: 8px;
  font-weight: bold;
  margin: 8px 0 0 0;
  display: inline-block;
`;

const StaffName = styled.div`
  font-size: 14px;
  text-transform: uppercase;
`;

const TimeTable = styled.div`
  margin-bottom: 10px;
`;

const TableHeader = styled.div`
  display: flex;
  padding: 6px 0px;
  border-bottom: 1px dashed black;
  font-size: 10px;
  font-weight: 600;
`;

const HeaderCell = styled.div<{ isFirstColumn?: boolean }>`
  flex: 1;
  text-align: ${props => props.isFirstColumn ? 'left' : 'right'};
  font-family: 'Open Sans';
  font-size: 14px;
`;

const TableRow = styled.div`
  display: flex;
  padding: 4px 0px;
  font-size: 14px;
`;

const TableCell = styled.div<{ isFirstColumn?: boolean }>`
  flex: 1;
  text-align: ${props => props.isFirstColumn ? 'left' : 'right'};
  font-family: 'Open Sans';
  font-size: 14px;
`;

const SectionDivider = styled.hr`
  border: none;
  border-top: 1px solid black;
  margin: 8px 0;
`;